/* eslint-disable no-await-in-loop */
/* eslint-disable  @typescript-eslint/no-explicit-any */
import { fetch as defaultFetch, Headers } from './index.js';
import { withRetries, withTraces, withOkayStatus, withHttpKeepAlive } from './middleware/index.js';
const enableKeepAlive = globalThis?.process?.env?.CVENT_FETCH_KEEPALIVE_EXPERIMENT === '1';
/**
 *
 *  Base Abstract Fetch client.  Takes options and wraps and provides a modified fetch function as a method.
 *
 */
export class BaseFetchClient {
    constructor(options = {}) {
        const { fetch = defaultFetch, init = { headers: new Headers() }, preRequest = enableKeepAlive ? [withHttpKeepAlive(), withTraces()] : [withTraces()], postResponse = [withRetries(), withOkayStatus()], networkError, timeout } = options;
        // Optionally wrap `fetch` with network error handling
        this._fetch = networkError ? this.withNetworkErrorHandling(fetch) : fetch;
        this._postResponse = postResponse;
        this._preRequest = preRequest;
        this._networkError = networkError;
        this._defaultInit = init;
        this._timeout = timeout;
    }
    /**
     * Exposes fetch function to support legacy unit tests
     * @returns fetch
     */
    get __internalFetch() {
        // eslint-disable-next-line dot-notation
        if (process?.env['NODE_ENV'] !== 'test') {
            throw new Error('Access to private fetch function is only allowed in test environments');
        }
        return this._fetch;
    }
    /**
     * Wrap a fetch implementation with network error handling
     * @param fetchFn fetch implementation
     * @returns wrapped fetch function
     */
    withNetworkErrorHandling(fetchFn) {
        return async (url, init) => {
            let response;
            try {
                // Attempt the fetch request
                response = await fetchFn(url, init);
            }
            catch (err) {
                // Rethrow error when the request was aborted
                if (err.name === 'AbortError') {
                    throw err;
                }
                // Rethrow the error if no error retry middleware is configured
                if (!this._networkError) {
                    throw err;
                }
                // Invoke network error retry middleware
                response = await this._networkError({
                    error: err,
                    fetch: fetchFn,
                    url,
                    init
                });
            }
            return response;
        };
    }
    /**
     *  Wrapped fetch function that will run with configuration options
     * (applying any provided request or response middleware)
     * @param url
     * @param init
     * @returns
     */
    async fetch(url, init) {
        // create new headers in case they are a Header instead of an Record<string,string>
        const headers = new Headers(this._defaultInit.headers);
        if (init) {
            const { headers: requestHeaders = {} } = init;
            new Headers(requestHeaders).forEach((v, k) => {
                headers.set(k, v);
            });
        }
        let signal;
        let timer;
        if (this._timeout) {
            /*
             * This is a workaround for test environments using jsdom (used by jest-environment-jsdom),
             * which does not include an implementation of AbortSignal.timeout
             * https://github.com/jsdom/jsdom/issues/3516
             */
            try {
                signal = AbortSignal.timeout(this._timeout);
            }
            catch (err) {
                if (err instanceof TypeError) {
                    const abortController = new AbortController();
                    signal = abortController.signal;
                    timer = setTimeout(() => {
                        abortController.abort();
                    }, this._timeout);
                }
            }
        }
        // add default headers if not set explicitly
        let fetchParams = [
            url,
            {
                ...this._defaultInit,
                ...init,
                headers,
                signal
            }
        ];
        // run each pre fetch middleware request options
        for (const pre of this._preRequest) {
            fetchParams = (await pre(...fetchParams)) || fetchParams;
        }
        let response;
        // call fetch with potentially new params
        response = await this._fetch(...fetchParams);
        // run each post fetch middleware
        for (const post of this._postResponse) {
            response =
                (await post({
                    fetch: this._fetch,
                    url: fetchParams[0],
                    init: fetchParams[1],
                    response
                })) || response;
        }
        clearTimeout(timer);
        // return final response
        return response;
    }
    /**
     *
     *  Helper method to  call fetch with body and forces method to 'POST'
     * @param url  - url to use
     * @param body - body to use
     * @param init - additional fetch RequestInit options
     * @returns
     */
    async post(url, body, init) {
        return this.fetch(url, { body, method: 'POST', ...init });
    }
    /**
     *
     *  Helper method to call fetch with no body, forces method to 'GET'
     * @param url  - url to use
     * @param body - body to use
     * @param init - additional fetch RequestInit options
     * @returns
     */
    get(url, init) {
        return this.fetch(url, { method: 'GET', ...init });
    }
    /**
     *
     *  Helper method to call fetch with body and forces method to 'PATCH'
     * @param url  - url to use
     * @param body - body to use
     * @param init - additional fetch RequestInit options
     * @returns
     */
    patch(url, body, init) {
        return this.fetch(url, { body, method: 'PATCH', ...init });
    }
    /**
     *
     *  Helper method to call fetch with optional body and forces method to 'DELETE'
     * @param url  - url to use
     * @param body - body to use
     * @param init - additional fetch RequestInit options
     * @returns
     */
    delete(url, body, init) {
        return this.fetch(url, { body, method: 'DELETE', ...init });
    }
    /**
     *
     *  Helper method to call fetch with body and forces method to 'PUT'
     * @param url  - url to use
     * @param body - body to use
     * @param init - additional fetch RequestInit options
     * @returns
     */
    put(url, body, init) {
        return this.fetch(url, { body, method: 'PUT', ...init });
    }
    /**
     *
     *  Helper method to call fetch forces method to 'OPTIONS'
     * @param url  - url to use
     * @param body - body to use
     * @param init - additional fetch RequestInit options
     * @returns
     */
    options(url, init) {
        return this.fetch(url, { method: 'OPTIONS', ...init });
    }
    /**
     *
     * @returns return a JsonFetchClient using same configured FetchOptions
     */
    json() {
        // eslint-disable-next-line  no-use-before-define
        return new JsonClient({
            fetch: this._fetch,
            preRequest: this._preRequest,
            postResponse: this._postResponse,
            init: this._defaultInit,
            timeout: this._timeout
        });
    }
}
/**
 *  A Fetch client that exposes fetch and optionally a more specialized clients or helper functions (see JsonClient)
 */
export class FetchClient extends BaseFetchClient {
    constructor(options = {}) {
        super(options);
    }
    /**
     * Fetch, but wrapped in provided FetchOptions
     * @param url
     * @param init
     * @returns
     */
    async fetch(url, init) {
        return super.fetch(url, init);
    }
    /**
     *
     * @returns A JsonClient that used the same underlying FetchOptions
     */
    json() {
        return super.json();
    }
}
/**
 *  Typescript friendly Fetch client that doesn't expose fetch and assumes Json Payloads and Json Responses
 */
export class JsonClient extends BaseFetchClient {
    constructor(options = {}) {
        super(options);
    }
    /**
     * serialize body via stringify and force content-type to application/json
     * @param body
     * @param init
     * @returns
     */
    static prepJsonFetch(body, init) {
        const { headers: requestHeaders = {} } = init || {};
        const headers = new Headers(requestHeaders);
        headers.set('content-type', 'application/json');
        const jsonBody = JSON.stringify(body);
        return { jsonBody, headers };
    }
    static postJsonFetch(response) {
        if (response.status === 204) {
            return undefined;
        }
        return response.json();
    }
    async post(url, body, init) {
        const { jsonBody, headers } = JsonClient.prepJsonFetch(body, init);
        const response = await super.post(url, jsonBody, { ...init, headers });
        return JsonClient.postJsonFetch(response);
    }
    async get(url, init) {
        const response = await super.get(url, init);
        return JsonClient.postJsonFetch(response);
    }
    async delete(url, body, init) {
        const { jsonBody, headers } = JsonClient.prepJsonFetch(body, init);
        const response = await super.delete(url, jsonBody, { ...init, headers });
        return JsonClient.postJsonFetch(response);
    }
    async put(url, body, init) {
        const { jsonBody, headers } = JsonClient.prepJsonFetch(body, init);
        const response = await super.put(url, jsonBody, { ...init, headers });
        return JsonClient.postJsonFetch(response);
    }
    async patch(url, body, init) {
        const { jsonBody, headers } = JsonClient.prepJsonFetch(body, init);
        const response = await super.patch(url, jsonBody, { ...init, headers });
        return JsonClient.postJsonFetch(response);
    }
}
//# sourceMappingURL=FetchClient.js.map